import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { useDeleteModal } from '~/eds/src/components/hooks/useDeleteModal';
import { useDeletePermanentModal } from '~/eds/src/components/hooks/useDeletePermanentModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { useConversationPipeline } from '~/features/ask-anything/hooks/useConversationPipeline';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { Question, QuestionGroup } from '~/types/QuestionGroup';
import { Uuid } from '~/types/Uuid';

import { QuestionList } from '../Questions/QuestionList';
import { QuestionGroupItem } from './QuestionGroupItem';
import { useQuestionGroupFormModal } from './useQuestionGroupFormModal';

interface QuestionGroupListProps {
  questionGroups: QuestionGroup[];
  runningQuestionGroupId?: string;
  disableRun?: boolean;
  onQuestionClick: (question: Question, questionGroupId: Uuid) => void;
  onQuestionGroupClick: (
    questionGroup: QuestionGroup,
    questions: Question[],
  ) => void;
}

export const QuestionGroupList = ({
  questionGroups,
  runningQuestionGroupId,
  disableRun,
  onQuestionClick,
  onQuestionGroupClick,
}: QuestionGroupListProps) => {
  const intl = useIntl();

  const modalTitle = intl.formatMessage({
    id: 'QuestionGroupList.deleteModalTitle',
    defaultMessage: 'Delete Question Group',
    description: 'Title for delete question group modal',
  });
  const [getQuestionGroupQuestions, { isFetching: isFetchingQuestions }] =
    api.endpoints.getQuestionGroupQuestions.useLazyQuery();
  const [questionGroupQuestionsMapping, setQuestionGroupQuestionsMapping] =
    useState<
      Record<
        string,
        {
          data: Question[];
          isLoading: boolean;
        }
      >
    >({});

  const [actionData, setActionData] = useState<{
    action: 'edit' | 'delete' | null;
    questionGroup: Nullable<QuestionGroup>;
  }>({
    action: null,
    questionGroup: null,
  });

  const [editModal, showEditModal] = useQuestionGroupFormModal({
    questionGroup: actionData.questionGroup,
    onClose: () => setActionData({ action: null, questionGroup: null }),
  });

  const { queue: messageQueue } = useConversationPipeline();

  const getQuestionStatus = (questionId: string, questionGroupId: string) => {
    if (runningQuestionGroupId === questionGroupId) {
      return messageQueue.find((message) => message.id === questionId)?.status;
    }
    return 'idle';
  };

  const getQuestionGroupStatus = (questionGroupId: string) => {
    if (runningQuestionGroupId === questionGroupId) {
      return messageQueue.every((question) => question.status === 'complete')
        ? 'complete'
        : 'running';
    }
    return 'idle';
  };

  const [deleteQuestionGroup, { isLoading: isDeleting }] =
    api.endpoints.deleteQuestionGroup.useMutation();

  const { toast } = useToast();

  const handleOnToggle = async (
    questionGroupId: string,
    isExpanded: boolean,
  ) => {
    // cleanup if the group is collapsed
    if (!isExpanded) {
      setQuestionGroupQuestionsMapping((prev) => ({
        ...prev,
        [questionGroupId]: {
          data: [],
          isLoading: false,
        },
      }));
      return;
    }
    try {
      setQuestionGroupQuestionsMapping((prev) => ({
        ...prev,
        [questionGroupId]: {
          data: [],
          isLoading: true,
        },
      }));
      const questions = await getQuestionGroupQuestions({
        id: questionGroupId,
      }).unwrap();
      if (questions) {
        setQuestionGroupQuestionsMapping((prev) => ({
          ...prev,
          [questionGroupId]: {
            data: questions,
            isLoading: false,
          },
        }));
      }
    } catch {
      toast({
        status: 'warning',
        title: intl.formatMessage({
          id: 'QuestionGroupList.errorTitle',
          defaultMessage: 'Error: Unable to Load Questions',
          description: 'Error title when questions fail to load',
        }),
        message: intl.formatMessage({
          id: 'QuestionGroupList.errorMessage',
          defaultMessage: 'Failed to load questions for the selected group.',
          description: 'Error message when questions fail to load',
        }),
      });
    }
  };

  const handleDelete = async () => {
    if (!actionData.questionGroup) {
      return;
    }
    try {
      await deleteQuestionGroup({ id: actionData.questionGroup.id }).unwrap();
      toast({
        status: 'success',
        message: intl.formatMessage({
          id: 'QuestionGroupList.deleteSuccessMessage',
          defaultMessage: 'The question group has been successfully deleted.',
          description: 'Success message when question group is deleted',
        }),
      });
      hideDeleteModal();
    } catch (apiError: any) {
      toast({
        status: 'danger',
        message: intl.formatMessage(
          {
            id: 'QuestionGroupList.deleteErrorMessage',
            defaultMessage: 'The delete action failed due to {errorTitle}.',
            description:
              'Error message when delete fails. Example: errorTitle = "Network Error"',
          },
          {
            errorTitle: apiError.errors[0].title,
          },
        ),
      });
    }
  };

  const [confirmationDeleteModal, showConfirmationDeleteModal] =
    useDeletePermanentModal({
      confirmText: 'DELETE',
      children: (
        <Text>
          {intl.formatMessage({
            id: 'QuestionGroupList.confirmDeleteSharedMessage',
            defaultMessage:
              "This item is shared with other members in your organization. Are you sure you want to delete the 1 item you have selected? You won't be able to undo this action.",
            description:
              'Confirmation message when deleting a shared question group',
          })}
        </Text>
      ),
      title: modalTitle,
      onDelete: handleDelete,
      isDeleting,
    });

  const [deleteModal, showDeleteModal, hideDeleteModal] = useDeleteModal({
    children: (
      <Text>
        {intl.formatMessage({
          id: 'QuestionGroupList.confirmDeleteMessage',
          defaultMessage:
            "Are you sure you want to delete the 1 item you have selected? You won't be able to undo this action.",
          description: 'Confirmation message when deleting a question group',
        })}
      </Text>
    ),
    isDeleting,
    primaryActionText: intl.formatMessage({
      id: 'QuestionGroupList.deleteAction',
      defaultMessage: 'Delete',
      description: 'Delete button text',
    }),
    onDelete: handleDelete,
    title: modalTitle,
  });

  useEffect(() => {
    switch (actionData.action) {
      case 'edit':
        showEditModal();
        break;
      case 'delete':
        actionData.questionGroup?.visibility === 'PRIVATE'
          ? showDeleteModal()
          : showConfirmationDeleteModal();
        break;
      default:
        break;
    }
  }, [actionData]);

  const getQuestionGroupItems = () => {
    return questionGroups.map((questionGroup) => {
      const questionData = questionGroupQuestionsMapping[questionGroup.id] ?? {
        data: null,
        isLoading: false,
      };

      if (questionData.data) {
        questionData.data = questionData.data.map((question) => {
          return {
            ...question,
            status: getQuestionStatus(question.id, questionGroup.id),
          };
        });
      }

      const questionGroupStatus = getQuestionGroupStatus(questionGroup.id);

      return {
        title: questionGroup.name,
        headerBorder: 'bottom' as const,
        headerContentAlignment: 'start' as const,
        headerBorderType: 'rounded' as const,
        summary: (
          <QuestionGroupItem
            questionGroup={questionGroup}
            onAction={setActionData}
            disableRun={!!disableRun || isFetchingQuestions}
            status={questionGroupStatus}
            onQuestionGroupClick={async () => {
              if (questionData.data && questionData.data.length) {
                onQuestionGroupClick(questionGroup, questionData.data);
              } else {
                setQuestionGroupQuestionsMapping((prev) => ({
                  ...prev,
                  [questionGroup.id]: {
                    data: [],
                    isLoading: true,
                  },
                }));
                const questions = await getQuestionGroupQuestions({
                  id: questionGroup.id,
                }).unwrap();
                if (questions) {
                  setQuestionGroupQuestionsMapping((prev) => ({
                    ...prev,
                    [questionGroup.id]: {
                      data: questions,
                      isLoading: false,
                    },
                  }));
                  onQuestionGroupClick(questionGroup, questions);
                }
              }
            }}
          />
        ),
        isLoading: questionData.isLoading,
        details: (
          <Layout py={2} ml={54}>
            <Text variant="tiny" color="text.secondary">
              {questionGroup.description}
            </Text>
          </Layout>
        ),
        content: questionGroup.questionIds.length ? (
          <QuestionList
            questions={questionData.data || []}
            onQuestionClick={(question) =>
              onQuestionClick(question, questionGroup.id)
            }
            disableRun={!!disableRun}
            disableActions
          />
        ) : (
          <StatusMessage
            status="info"
            title={intl.formatMessage({
              id: 'QuestionGroupList.noQuestionsTitle',
              defaultMessage: 'No questions in the group',
              description: 'Title when a question group has no questions',
            })}
            mode="column"
            message={intl.formatMessage({
              id: 'QuestionGroupList.noQuestionsMessage',
              defaultMessage:
                'Click on More Options > Edit to add questions to the question group.',
              description: 'Message when a question group has no questions',
            })}
            action={{
              level: 'action',
              text: intl.formatMessage({
                id: 'QuestionGroupList.addQuestionsAction',
                defaultMessage: 'Add Questions',
                description: 'Action button text to add questions to group',
              }),
              onClick: () => setActionData({ action: 'edit', questionGroup }),
            }}
          />
        ),
        data: questionGroup,
      };
    });
  };

  return (
    <Layout direction="column" overflowY="auto" height="100%" flex={1}>
      <Accordion<QuestionGroup>
        border="none"
        padding="none"
        items={getQuestionGroupItems()}
        onToggle={(isExpanded, item) =>
          handleOnToggle(item?.data?.id || '', isExpanded)
        }
      />
      {editModal}
      {deleteModal}
      {confirmationDeleteModal}
    </Layout>
  );
};
